import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button, ButtonColor } from './Button'
import { showToast } from 'src/utils/toast'

interface Props {
  isOpen?: boolean
  title: string
  text?: string
  imageSrc: string
  submitLabel: string
  color?: ButtonColor
  onSubmit(): void | Promise<void>
  onClose(): void
}

const SensitiveActionWithPictureModalComponent = ({
  isOpen,
  title,
  text,
  imageSrc,
  submitLabel,
  color = 'red',
  onSubmit,
  onClose,
}: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    try {
      await onSubmit()
    } catch (err) {
      showToast(err as any, { variant: 'error' })
    }

    setIsLoading(false)
  }, [onSubmit])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div css={tw`w-[385px] flex-col bg-white rounded-lg`}>
        <div css={tw`px-6 py-4 flex flex-col items-center`}>
          <div css={tw`flex justify-center`}>
            <img src={imageSrc} alt="Confirmation" css={tw`w-[210px] h-[164px] object-contain`} />
          </div>
          <Typography variant="h4" css={tw`text-center`}>
            {title}
          </Typography>
          {text && (
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`text-center mt-0.5`}>
              {text}
            </Typography>
          )}
        </div>
        <div
          css={tw`flex justify-between items-center px-4 py-3 border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button
            className={tw`px-8`}
            disabled={isLoading}
            onClick={onClose}
            color="grey"
            size="small"
            data-testid="sensitive-action-with-picture-modal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className={tw`px-8`}
            loading={isLoading}
            onClick={handleSubmit}
            color={color}
            size="small"
            data-testid="sensitive-action-with-picture-modal-submit-btn"
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const SensitiveActionWithPictureModal = memo(SensitiveActionWithPictureModalComponent)
